<template>
  <v-dialog v-model="isOpen" width="800" persistent>
    <v-form ref="form" v-model="valid" @submit.prevent="save">
      <v-card>
        <v-card-title>{{ dialogTitle }}</v-card-title>
        <v-card-text>
          <v-alert v-if="hasNonFieldErrors" type="error">
            <ul>
              <li v-for="error in nonFieldErrors" :key="error">{{ error }}</li>
            </ul>
          </v-alert>
          <v-autocomplete
            v-if="hasMeetingChoices"
            v-model="meeting"
            :items="meetingChoices"
            :label="$t('Business_case_meeting')"
            :rules="[$rules.required]"
            item-text="title"
            item-value="id"
            return-object
            required
            clearable
            :error-messages="errorsForField('meeting')"
          />
          <v-autocomplete
            v-model="vorstoss.business_type"
            :items="businessTypeChoices"
            :label="$t('Business_type')"
            :rules="[$rules.required]"
            return-object
            required
            clearable
            :error-messages="errorsForField('business_type')"
          />
          <v-text-field
            v-model="vorstoss.title"
            :label="$t('Business_case_title')"
            :rules="[$rules.required]"
            required
            :error-messages="errorsForField('title')"
          />
          <v-textarea
            v-model="vorstoss.description"
            :label="$t('Wording')"
            :rules="[$rules.required]"
            required
            :error-messages="errorsForField('description')"
          />
          <v-textarea v-model="vorstoss.reason" :label="$t('Business_case_reason')" />
          <v-autocomplete
            v-model="firstSignatoryId"
            :items="committeeMembers"
            :label="$t('Business_case_proposal_creator')"
            :rules="[$rules.required]"
            required
            :disabled="!user.can_create_proposals_for_other_people"
            item-value="id"
            :item-text="signatoryLabel"
            return-object
          />
          <v-autocomplete
            v-model="secondSignatoryId"
            :items="committeeMembers"
            :label="$t('Business_case_second_signature')"
            clearable
            item-value="id"
            :item-text="signatoryLabel"
            return-object
          />
          <v-autocomplete
            v-model="thirdSignatoryId"
            :items="committeeMembers"
            :label="$t('Business_case_third_signature')"
            clearable
            item-value="id"
            :item-text="signatoryLabel"
            return-object
          />
          <v-autocomplete
            v-model="internalOtherSignatories"
            :items="otherSignatoryChoices"
            :label="$t('Business_case_other_signature')"
            clearable
            :item-value="otherSignatoryValue"
            :item-text="otherSignatoryLabel"
            return-object
            multiple
          />
          <v-select
            v-model="vorstoss.rights_holder"
            :items="rightsHolderOptions"
            :label="$t('Business_case_rights_holder')"
            clearable
          />
          <div class="d-flex">
            <v-checkbox v-model="vorstoss.is_urgent" class="mr-3" :label="$t('mark_urgent')" />
          </div>

          <v-file-input
            v-model="attachments"
            small-chips
            multiple
            :label="$t('Select_file')"
            @change="handleFileChange"
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text color="error" @click="cancel">{{ $t('Cancel') }}</v-btn>
          <v-btn text type="submit" data-testid="save-vorstoss" color="primary">{{ saveActionLabel }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isUndefined from 'lodash/isUndefined'
import { api } from '@/api'
import { createLink } from '@/api/helpers'
import Committee from '@/store/models/committee'
import Meeting from '@/store/models/meeting'
import Profile from '@/store/models/profile'
import User from '@/store/models/user'

function vorstossFactory() {
  return {
    first_signatory: { id: null, signatory_id: Profile.query().first().id, role: 'FIRST_SIGNATORY', type: 'user' },
    other_signatories: [],
  }
}

function memberLabel(member) {
  return [member.first_name, member.last_name].filter(Boolean).join(' ') + ` (${member.username})`
}

export default {
  name: 'VorstossDialog',
  data() {
    return {
      isOpen: false,
      initial: false,
      vorstoss: {},
      valid: null,
      rightsHolderOptions: [
        { text: this.$t('Business_case_proposal_creator'), value: 'proposal_creator' },
        { text: this.$t('Business_case_faction'), value: 'faction' },
        { text: this.$t('Business_case_commission'), value: 'commission' },
        { text: this.$t('Business_case_cross_party'), value: 'cross_party' },
      ],
      errors: {},
      businessTypeChoices: [],
      meetingChoices: [],
      meeting: {},
      attachments: [],
      isAttachmentChanged: false,
      otherSignatoryTypes: {
        user: this.$t('member'),
        committee: this.$t('Committee'),
        committeegroup: this.$t('Committee_Group'),
      },
      committees: [],
      committeeGroups: [],
      loadingAsyncData: true,
      firstSignatoryId: Profile.query().first().id,
      secondSignatoryId: null,
      thirdSignatoryId: null,
      internalOtherSignatories: [],
    }
  },
  computed: {
    committeeMembers() {
      return User.all().filter((user) => user.roles.includes('member'))
    },
    otherSignatoryChoices() {
      const members = this.committeeMembers.map((member) => ({
        id: member.id,
        label: memberLabel(member),
        type: 'user',
      }))
      const committees = this.committees.map((committee) => ({
        id: committee.id,
        label: committee.title,
        type: 'committee',
      }))
      const committeeGroups = this.committeeGroups.map((committeeGroup) => ({
        id: committeeGroup.id,
        label: committeeGroup.title,
        type: 'committeegroup',
      }))
      return [...committees, ...committeeGroups, ...members]
    },
    user() {
      return Profile.query().first()
    },
    nonFieldErrors() {
      return this.errorsForField('non_field_errors')
    },
    hasNonFieldErrors() {
      return !isEmpty(this.nonFieldErrors)
    },
    isInEditMode() {
      return !isUndefined(this.vorstoss.id)
    },
    dialogTitle() {
      return this.isInEditMode ? this.$t('updateVorstossTitle') : this.$t('createVorstossTitle')
    },
    saveActionLabel() {
      return this.isInEditMode ? this.$t('modifyVorstossButton') : this.$t('newVorstossButton')
    },
    saveSuccessMessage() {
      return this.isInEditMode ? this.$t('vorstossCardModifySuccess') : this.$t('vorstossCardCreateSuccess')
    },
    saveErrorMessage() {
      return this.isInEditMode ? this.$t('vorstossCardModifyError') : this.$t('vorstossCardCreateError')
    },
    hasMeetingChoices() {
      return !isEmpty(this.meetingChoices)
    },
  },
  watch: {
    firstSignatoryId(signatory) {
      this.vorstoss.first_signatory = this.transform_user_signatory(signatory, 'FIRST_SIGNATORY')
    },
    secondSignatoryId(signatory) {
      this.vorstoss.second_signatory = this.transform_user_signatory(signatory, 'SECOND_SIGNATORY')
    },
    thirdSignatoryId(signatory) {
      this.vorstoss.third_signatory = this.transform_user_signatory(signatory, 'THIRD_SIGNATORY')
    },
    internalOtherSignatories(newList) {
      const selectedKeys = new Set(newList.map((signatory) => `${signatory.type}:${signatory.id}`))

      this.vorstoss.other_signatories = this.vorstoss.other_signatories.filter((signatory) => {
        if (signatory.role !== 'OTHER_SIGNATORY') return true
        const key = `${signatory.type}:${signatory.id}`
        return selectedKeys.has(key)
      })

      for (const signatory of newList) {
        const key = `${signatory.type}:${signatory.id}`
        const alreadyExists = this.vorstoss.other_signatories.some(
          (existing) => existing.role === 'OTHER_SIGNATORY' && `${existing.type}:${existing.id}` === key,
        )
        if (!alreadyExists) {
          this.vorstoss.other_signatories.push({
            id: null,
            role: 'OTHER_SIGNATORY',
            signatory_id: signatory.id,
            type: signatory.type,
          })
        }
      }
    },
  },
  async mounted() {
    try {
      const businessTypes = await api.get(createLink('businesstypes'), { params: { show_for_proposal: true } })
      this.businessTypeChoices = businessTypes.data.map((businesstype) => ({
        text: businesstype.title,
        value: businesstype.api_uid,
      }))
    } finally {
      this.loadingAsyncData = false
    }
  },

  methods: {
    resetSignatoryIds() {
      this.firstSignatoryId = Profile.query().first().id
      this.secondSignatoryId = null
      this.thirdSignatoryId = null
    },
    handleFileChange(files) {
      this.attachments = files
      this.isAttachmentChanged = true
    },
    signatoryLabel(signatory) {
      return memberLabel(signatory)
    },
    otherSignatoryValue(signatory) {
      const signatoryType = get(this.otherSignatoryTypes, signatory.type, 'unknown')
      return `${signatory.id} - ${signatoryType}`
    },
    otherSignatoryLabel(signatory) {
      const signatoryType = get(this.otherSignatoryTypes, signatory.type, 'unknown')
      return `${signatory.label} - ${signatoryType}`
    },
    getIdForVorstossSignatory(role) {
      switch (role) {
        case 'FIRST_SIGNATORY':
          return this.vorstoss.first_signatory?.vorstoss_signatory_id || null
        case 'SECOND_SIGNATORY':
          return this.vorstoss.second_signatory?.vorstoss_signatory_id || null
        case 'THIRD_SIGNATORY':
          return this.vorstoss.third_signatory?.vorstoss_signatory_id || null
        default:
          return null
      }
    },
    transform_user_signatory(user, role) {
      if (!user) return null
      return {
        id: this.getIdForVorstossSignatory(role),
        signatory_id: user.id,
        role,
        type: 'user',
      }
    },
    cancel() {
      this.$refs.form.resetValidation()
      this.isOpen = false
    },
    errorsForField(field) {
      return get(this.errors, field, [])
    },
    async attachAttachments(vorstoss) {
      const formData = new FormData()
      this.attachments.forEach((attachment) => formData.append('attachments', attachment))
      await api.patch(createLink('vorstoss/{id}/attach_attachments', { id: vorstoss.id }), formData)
    },
    async save() {
      try {
        const { data: vorstoss } = this.isInEditMode
          ? await api.patch(createLink('vorstoss/{id}', { id: this.vorstoss.id }), this.vorstoss)
          : await api.post(createLink('vorstoss'), { meeting: this.meeting.id, ...this.vorstoss })

        if (this.isAttachmentChanged) {
          await this.attachAttachments(vorstoss)
        }

        this.$emit('update')
        this.notifySuccess(this.saveSuccessMessage)
        this.cancel()
      } catch (error) {
        this.errors = get(error, 'response.data')
        this.notifyError(this.saveErrorMessage)
        throw error
      }
    },
    async openWithCommittee(committee) {
      await User.fetchAllByModel(committee)
      await Meeting.fetchAllByModel(committee)
      this.meetingChoices = Meeting.query().where('committee_id', committee.id).all()
      this.vorstoss = vorstossFactory()
      this.resetSignatoryIds()
      this.isOpen = true
    },
    async openWithMeeting(meeting) {
      this.meeting = meeting
      const committee = await Committee.findOrFetch(meeting.committee_id)
      await User.fetchAllByModel(committee)
      this.vorstoss = vorstossFactory()
      const { data: committees } = await api.get(createLink('committees/listing'))
      const { data: committeeGroups } = await api.get(
        createLink('committees/{id}/committee_groups', { id: committee.id }),
      )
      this.committees = committees
      this.committeeGroups = committeeGroups
      this.resetSignatoryIds()
      this.isOpen = true
    },
    async openWithVorstoss(vorstoss) {
      if (this.initial) {
        this.isOpen = true
        return
      }
      this.initial = true

      this.meeting = await Meeting.findOrFetch(vorstoss.meeting_id)
      await Committee.fetchAll()
      const committee = await Committee.query().find(this.meeting.committee_id)
      await User.fetchAllByModel(committee)
      this.vorstoss = vorstoss

      this.attachments = vorstoss.attachments
      // Fetch committee and groups
      const { data: committees } = await api.get(createLink('committees/listing'))
      const { data: committeeGroups } = await api.get(
        createLink('committees/{id}/committee_groups', { id: committee.id }),
      )
      this.committees = committees
      this.committeeGroups = committeeGroups
      this.resetSignatoryIds()

      this.firstSignatoryId = this.vorstoss.first_signatory
      this.secondSignatoryId = this.vorstoss.second_signatory
      this.thirdSignatoryId = this.vorstoss.third_signatory
      this.internalOtherSignatories = []

      const seen = new Set()
      this.vorstoss.other_signatories.forEach((signatory) => {
        const key = `${signatory.type}:${signatory.id}`
        if (!seen.has(key)) {
          seen.add(key)
        }
        this.internalOtherSignatories.push({
          id: signatory.id,
          type: signatory.type,
        })
      })

      this.vorstoss.other_signatories = this.vorstoss.other_signatories.map((signatory) => {
        return {
          id: signatory.vorstoss_signatory_id,
          role: 'OTHER_SIGNATORY',
          signatory_id: signatory.id,
          type: signatory.type,
        }
      })

      this.isOpen = true
    },
  },
}
</script>
